// CreateProposalForm — modal to create a holder proposal (EIP-712 signed).
// Must be rendered INSIDE a WagmiProvider (GovernancePage provides it).
import React, { useState } from "react";
import { useAccount, useSignTypedData } from "wagmi";
import { X, Loader2, CheckCircle2, Eye, PencilLine } from "lucide-react";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tGov } from "../../i18n/governance";
import { buildProposalTypedData, GOV_PARAMS } from "../../web3/governanceSig";

const TYPES = [
  "course_add", "course_remove", "feature_request", "vault_params",
  "burn_rate", "mentor_admit", "partnership", "treasury", "other",
];

export default function CreateProposalForm({ onClose, onCreated }) {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const { token } = useAuth();
  const { address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();

  const [title, setTitle] = useState("");
  const [type, setType] = useState("feature_request");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(7);
  const [preview, setPreview] = useState(false);
  const [phase, setPhase] = useState(null); // 'signing' | 'publishing'
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(false);

  const valid = title.trim().length >= 8 && description.trim().length >= 20;

  const submit = async () => {
    setError(null);
    try {
      setPhase("signing");
      const { typedData, timestamp } = buildProposalTypedData(title.trim(), address);
      const signature = await signTypedDataAsync(typedData);
      setPhase("publishing");
      const r = await fetch(`${API}/governance/proposals`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          title: title.trim(), description: description.trim(),
          type, duration_days: duration,
          wallet_address: address, signature, timestamp,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail?.error || "create_failed");
      setCreated(true);
      onCreated?.();
    } catch (e) {
      const code = e?.message;
      setError(tGov(lang, `err_${code}`) !== `err_${code}` ? tGov(lang, `err_${code}`) : (e?.shortMessage || code));
    } finally {
      setPhase(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm md:items-center"
      onClick={onClose} data-testid="gov-create-overlay">
      <div className="w-full max-w-xl rounded-3xl border border-white/15 bg-[#0B0F19] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="gov-create-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black text-white">{t("createProposal")}</h2>
          <button onClick={onClose} data-testid="gov-create-close" className="rounded-full p-1.5 text-white/40 hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {created ? (
          <div className="py-10 text-center" data-testid="gov-create-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <p className="mt-3 text-sm font-black text-white">{t("proposalCreated")}</p>
            <button onClick={onClose} className="mt-4 rounded-full bg-purple-400 px-6 py-2 text-xs font-black uppercase text-black">OK</button>
          </div>
        ) : preview ? (
          <div className="mt-4" data-testid="gov-create-preview">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-purple-300/80">{t("preview")}</p>
            <h3 className="mt-2 text-xl font-black text-white">{title || "—"}</h3>
            <p className="mt-0.5 text-[11px] text-white/45">
              <span className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[9px] uppercase">{t(`type_${type}`)}</span>
              <span className="ml-2">{duration} {t("days")}</span>
            </p>
            <div className="mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm text-white/70">
              {description || "—"}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => setPreview(false)} data-testid="gov-create-edit"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-xs font-black uppercase tracking-[0.18em] text-white/70 hover:bg-white/[0.06]">
                <PencilLine className="h-4 w-4" /> {t("edit")}
              </button>
              <button onClick={submit} disabled={!!phase || !valid} data-testid="gov-create-submit"
                className="rounded-xl bg-gradient-to-r from-purple-400 to-fuchsia-500 py-3 text-xs font-black uppercase tracking-[0.18em] text-black disabled:opacity-40">
                {phase ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {phase === "signing" ? t("signingWallet") : t("publishing")}
                  </span>
                ) : t("signAndPublish")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{t("formTitle")}</label>
              <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)}
                placeholder={t("formTitlePh")} data-testid="gov-create-title"
                className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm font-bold text-white outline-none placeholder:text-white/25 focus:border-purple-400/60" />
              <p className="mt-0.5 text-right font-mono text-[9px] text-white/30">{title.length}/100</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{t("proposalType")}</label>
                <select value={type} onChange={(e) => setType(e.target.value)} data-testid="gov-create-type"
                  className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-3 text-sm text-white outline-none focus:border-purple-400/60">
                  {TYPES.map((ty) => <option key={ty} value={ty} className="bg-[#0B0F19]">{t(`type_${ty}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{t("duration")}</label>
                <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="gov-create-duration"
                  className="mt-1 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-3 text-sm text-white outline-none focus:border-purple-400/60">
                  {GOV_PARAMS.DURATIONS.map((d) => <option key={d} value={d} className="bg-[#0B0F19]">{d} {t("days")}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{t("formDescription")}</label>
              <textarea value={description} maxLength={2000} rows={6} onChange={(e) => setDescription(e.target.value)}
                placeholder={t("formDescriptionPh")} data-testid="gov-create-description"
                className="mt-1 w-full resize-y rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-purple-400/60" />
              <p className="mt-0.5 text-right font-mono text-[9px] text-white/30">{description.length}/2000</p>
            </div>
            <button onClick={() => setPreview(true)} disabled={!valid} data-testid="gov-create-preview-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-purple-400/40 bg-purple-400/10 py-3 text-xs font-black uppercase tracking-[0.18em] text-purple-200 hover:bg-purple-400/20 disabled:opacity-40">
              <Eye className="h-4 w-4" /> {t("preview")}
            </button>
          </div>
        )}

        {error && (
          <p className="mt-3 break-words rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-300" data-testid="gov-create-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
